<template lang="pug">
.bulk-send-entries-card-table
  card-table(
    :total-items='rows.length'
    breakpoint-size='lg'
    v-if='rows.length'
  )
    template(v-for='(row, index) in rows' v-slot:[`card-${index}`]='{}')
      .position-relative
        .mb-1 {{ $t('bulkSendDetails.table.line') }}: {{ row.line }}
        .mb-1.text-break(
          :key='recipient.prefix'
          v-for='recipient in row.recipients'
        ) {{ $t('bulkSendDetails.table.recipients') }}: {{ recipient.name || '—' }} ({{ recipient.contact }})
        .mb-1 {{ $t('bulkSendDetails.table.status') }}: {{ getStatusText(row.displayStatus) }}
        .mb-1(v-if='row.failure_reason') {{ row.failure_reason }}
        .mb-1
          | {{ $t('bulkSendDetails.table.envelope') }}:
          |
          router-link.view-envelope(
            :to='getEnvelopeRoute(row.envelope)'
            v-if='row.envelope'
          ) {{ $t('bulkSendDetails.viewEnvelope') }}
          template(v-else) —
    template(v-slot:table-head)
      th(style='width: 8%') {{ $t('bulkSendDetails.table.line') }}
      th(style='width: 47%') {{ $t('bulkSendDetails.table.recipients') }}
      th(style='width: 30%') {{ $t('bulkSendDetails.table.status') }}
      th(style='width: 15%') {{ $t('bulkSendDetails.table.envelope') }}
    template(v-for='(row, index) in rows' v-slot:[`table-row-${index}`]='{}')
      td.text-muted {{ row.line }}
      td
        .d-flex.flex-column.gap-2
          .d-flex.flex-column(
            :key='recipient.prefix'
            v-for='recipient in row.recipients'
          )
            span {{ recipient.name || '—' }}
            small.text-muted.text-break {{ recipient.contact }}
      td
        span.entry-status.d-inline-flex.align-items-center.gap-2.fw-bold(
          :class='row.displayStatus'
        )
          span.dot.rounded-circle
          | {{ getStatusText(row.displayStatus) }}
        small.d-block.text-muted(v-if='row.failure_reason') {{ row.failure_reason }}
      td
        router-link.view-envelope.text-nowrap(
          :to='getEnvelopeRoute(row.envelope)'
          v-if='row.envelope'
        ) {{ $t('bulkSendDetails.viewEnvelope') }}
        span.text-muted(v-else) —
  .text-center.py-5(v-else)
    img.img-fluid(src='/img/ilustrations/no-contacts-found.svg')
    p.font-color-blue.text-center.mt-4 {{ $t('message.no_results_found') }}
</template>

<script>
import CardTable from '@/ui/organisms/CardTable.vue';
import BulkSendEntryStatus from '@/enums/BulkSendEntryStatus';
import BulkSendService from '@/modules/business/services/BulkSendService';

export default {
  name: 'BulkSendEntriesCardTable',
  components: {
    CardTable,
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
    firstLine: {
      type: Number,
      default: 1,
    },
    status: {
      type: String,
      default: '',
    },
    search: {
      type: String,
      default: '',
    },
  },
  computed: {
    rows() {
      return this.data
        .map((entry, index) => ({
          ...entry,
          line: this.firstLine + index,
          recipients: BulkSendService.getRecipients(entry.data),
          displayStatus: BulkSendService.getEntryDisplayStatus(entry),
        }))
        .filter((entry) => !this.status || entry.status === this.status)
        .filter((entry) => this.matchesSearch(entry.recipients));
    },
  },
  methods: {
    getEnvelopeRoute(envelopeId) {
      return {
        name: 'business.documents.info',
        params: { id: envelopeId },
      };
    },
    getStatusText(status) {
      return BulkSendEntryStatus.getText(status, this.$i18n);
    },
    matchesSearch(recipients) {
      if (!this.search) {
        return true;
      }

      const search = this.search.toLowerCase();

      return recipients.some((recipient) =>
        [recipient.name, recipient.contact].some((value) =>
          String(value || '')
            .toLowerCase()
            .includes(search)
        )
      );
    },
  },
};
</script>

<style lang="stylus" scoped>
.entry-status
  --status-color: #C9D2D2
  &.processing
    --status-color: #F5A623
    color: #B7791F
  &.completed, &.signed
    --status-color: #0E7A6F
    color: #0E6B5F
  &.awaitingSignature
    --status-color: #F5A623
    color: #B7791F
  &.cancelled, &.expired
    --status-color: #8A9696
    color: #5F6B6B
  &.failed, &.halted
    --status-color: #C0392B
    color: #C0392B

.dot
  flex-shrink: 0
  width: 10px
  height: 10px
  background-color: var(--status-color)

.view-envelope
  font-size: 13px
  color: #0A6EA8
  &:hover
    color: #07537F
</style>
